import { useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { logActivity } from "@/lib/logActivity";
import Swal from "sweetalert2";
import type { Client } from "../types";

interface Props {
  client: Client;
  tenantId: string;
  onClientUpdated: (c: Client) => void;
}

const INPUT = "w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 placeholder-slate-300 outline-none focus:border-[#C8A96E]/60 focus:bg-white transition-all";

const STATUS_OPTS = [
  { value: "active",   label: "Active",   cls: "bg-emerald-400/15 text-emerald-600 border-emerald-400/25" },
  { value: "inactive", label: "Inactive", cls: "bg-amber-400/15 text-amber-600 border-amber-400/25" },
  { value: "archived", label: "Archived", cls: "bg-slate-200 text-slate-500 border-slate-300" },
];

export default function OverviewTab({ client, tenantId, onClientUpdated }: Props) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [form, setForm]       = useState<Client>(client);

  const statusCfg = (s: string) =>
    STATUS_OPTS.find((x) => x.value === s) ?? STATUS_OPTS[0];

  const set = (k: keyof Client, v: string) => setForm((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.full_name.trim()) {
      Swal.fire({ title: "Name required", icon: "warning", background: "#fff", color: "#0f172a" });
      return;
    }
    setSaving(true);
    const { data, error } = await supabase
      .from("clients")
      .update({
        full_name: form.full_name.trim(),
        email:     form.email.trim()    || null,
        phone:     form.phone.trim()    || null,
        address:   form.address.trim()  || null,
        notes:     form.notes.trim()    || null,
        status:    form.status,
        updated_at: new Date().toISOString(),
      })
      .eq("id", client.id)
      .eq("tenant_id", tenantId)
      .select()
      .maybeSingle();

    setSaving(false);
    if (error) {
      Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#fff", color: "#0f172a" });
      return;
    }
    if (data) {
      onClientUpdated(data as Client);
      logActivity({ tenantId, action: "updated", entity: "client", entityId: client.id, entityName: form.full_name });
    }
    setEditing(false);
    Swal.fire({ title: "Saved!", icon: "success", timer: 1200, showConfirmButton: false, background: "#fff", color: "#0f172a" });
  };

  const handleCancel = () => { setForm(client); setEditing(false); };

  const sc = statusCfg(client.status);

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Header card */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#C8A96E]/10 text-[#9a7840] font-bold text-xl shrink-0">
              {client.full_name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h2 className="text-slate-800 font-semibold text-lg leading-tight">{client.full_name}</h2>
              <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border mt-1 ${sc.cls}`}>
                {sc.label}
              </span>
            </div>
          </div>
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-500 hover:text-slate-800 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-pencil-line text-xs"></i>
              Edit
            </button>
          )}
        </div>

        {!editing ? (
          /* ── View mode ── */
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: "Email",    value: client.email,   icon: "ri-mail-line" },
              { label: "Phone",    value: client.phone,   icon: "ri-phone-line" },
              { label: "Address",  value: client.address, icon: "ri-map-pin-line", full: true },
              { label: "Member since", value: new Date(client.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }), icon: "ri-calendar-line" },
              { label: "Last updated",  value: new Date(client.updated_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }), icon: "ri-refresh-line" },
            ].map((f) => (
              <div key={f.label} className={f.full ? "col-span-2" : ""}>
                <p className="text-slate-400 text-[10px] uppercase tracking-wider mb-1 flex items-center gap-1">
                  <i className={`${f.icon} text-[10px]`}></i>
                  {f.label}
                </p>
                <p className="text-slate-700 text-sm">{f.value || "—"}</p>
              </div>
            ))}

            {client.notes && (
              <div className="col-span-2 bg-slate-50 rounded-lg border border-slate-100 px-4 py-3">
                <p className="text-slate-400 text-[10px] uppercase tracking-wider mb-1.5 flex items-center gap-1">
                  <i className="ri-sticky-note-line text-[10px]"></i>
                  Notes
                </p>
                <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-wrap">{client.notes}</p>
              </div>
            )}
          </div>
        ) : (
          /* ── Edit mode ── */
          <div className="space-y-4">
            <div>
              <label className="text-slate-500 text-xs uppercase tracking-wider mb-1.5 block">Full Name *</label>
              <input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className={INPUT} placeholder="Client name" autoFocus />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-slate-500 text-xs uppercase tracking-wider mb-1.5 block">Email</label>
                <input type="email" value={form.email ?? ""} onChange={(e) => set("email", e.target.value)} className={INPUT} placeholder="email@example.com" />
              </div>
              <div>
                <label className="text-slate-500 text-xs uppercase tracking-wider mb-1.5 block">Phone</label>
                <input value={form.phone ?? ""} onChange={(e) => set("phone", e.target.value)} className={INPUT} placeholder="+1 555 000 0000" />
              </div>
            </div>
            <div>
              <label className="text-slate-500 text-xs uppercase tracking-wider mb-1.5 block">Address</label>
              <input value={form.address ?? ""} onChange={(e) => set("address", e.target.value)} className={INPUT} placeholder="Street, City, State, ZIP" />
            </div>
            <div>
              <label className="text-slate-500 text-xs uppercase tracking-wider mb-2 block">Status</label>
              <div className="flex items-center gap-2">
                {STATUS_OPTS.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => set("status", s.value)}
                    className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-all cursor-pointer whitespace-nowrap ${
                      form.status === s.value ? s.cls : "bg-slate-50 text-slate-400 border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-slate-500 text-xs uppercase tracking-wider">Notes</label>
                <span className={`text-[10px] font-mono ${(form.notes?.length ?? 0) > 450 ? "text-amber-500" : "text-slate-300"}`}>
                  {form.notes?.length ?? 0}/500
                </span>
              </div>
              <textarea
                value={form.notes ?? ""}
                onChange={(e) => set("notes", e.target.value)}
                rows={3}
                maxLength={500}
                className={`${INPUT} resize-none`}
                placeholder="Internal notes..."
              />
            </div>
            <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-100">
              <button onClick={handleCancel} className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg transition-all cursor-pointer whitespace-nowrap">
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2 bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><i className="ri-save-line"></i> Save</>}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Meta card */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Client ID",  value: client.id.slice(0, 12) + "…", icon: "ri-fingerprint-line" },
          { label: "Created",    value: new Date(client.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }), icon: "ri-calendar-check-line" },
          { label: "Updated",    value: new Date(client.updated_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }), icon: "ri-refresh-line" },
        ].map((m) => (
          <div key={m.label} className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#C8A96E]/10 mb-2">
              <i className={`${m.icon} text-[#9a7840] text-sm`}></i>
            </div>
            <p className="text-slate-400 text-[10px] uppercase tracking-wider">{m.label}</p>
            <p className="text-slate-700 text-xs font-medium mt-0.5 font-mono">{m.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}